"use client";

import { Check, Copy, ArrowRight, Maximize2 } from "lucide-react";
import React, { memo, useState, useEffect } from "react";
import { useUser } from "@/components/providers/user-context";
import { useCanvas } from "@/components/ui/canvas/canvas";
import Image from "next/image";
import type { ComponentProps } from 'react';
import { useTheme } from "next-themes";

interface PreBlockProps extends ComponentProps<'pre'> {
  children?: React.ReactNode;
}

export const PreBlock = memo(function PreBlock({ children, className, ...props }: PreBlockProps) {
  const [copied, setCopied] = React.useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const { preferences } = useUser();
  // Get canvas state to check if it's open
  const { isExpanded: isCanvasExpanded } = useCanvas();
  const { theme } = useTheme();

  // Configuration for code preview
  const MAX_PREVIEW_LINES = 3;
  const MIN_LINES_FOR_COLLAPSE = 5; // Only show "View in canvas" for code longer than this
  const MIN_LINES_FOR_PREVIEW = 5;   // Only show preview if code is at least this many lines
  
  // Check if we're on a mobile or tablet device
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768); // 768px is standard md breakpoint
      setIsTablet(width >= 768 && width <= 1024); // Between md and lg breakpoints
    };
    
    // Check on initial render
    checkDevice();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkDevice);
    
    // Clean up
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Apply font size classes
  const fontSizeClasses = {
    small: "text-sm",
    medium: "text-base",
    large: "text-lg"
  };

  const code = React.Children.toArray(children).find(
    child => React.isValidElement(child) && child.type === 'code'
  ) as React.ReactElement | undefined;

  if (!code) {
    return <pre className={`${className} ${fontSizeClasses[preferences.fontSize || 'medium']}`} {...props}>{children}</pre>;
  }

  // Extract filename if present in className (e.g., "language-typescript name=filename.ts")
  const classNameStr = code.props?.className || '';
  const filenameMatch = classNameStr.match(/name=([^\s]+)/);
  const filename = filenameMatch ? filenameMatch[1].replace(/^hljs\s*/, '') : null;
  
  // Clean up the language class - remove any hljs prefix and language- prefix
  const language = (classNameStr.replace(/\s*name=[^\s]+/, '').replace('language-', '') || 'text')
    .replace(/^hljs\s+/, '')  // Remove hljs prefix if it exists
    .replace(/\bhljs\b/, ''); // Remove hljs if it appears as a separate word

  // Extract code text
  const extractText = (children: React.ReactNode): string => {
    return React.Children
      .toArray(children)
      .map(child => {
        if (typeof child === 'string') return child;
        if (React.isValidElement(child)) {
          return extractText(child.props.children);
        }
        return '';
      })
      .join('');
  };

  // Get full code text
  const fullCodeText = extractText(code.props.children);
  
  // Split code into lines
  const codeLines = fullCodeText.split('\n');
  
  // Toggle expanded state (for mobile)
  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Create highlighted version of the code, properly handling the truncation
  const getHighlightedCode = () => {
    // Always return the full code without truncation
    return code;
  };

  // Get the highlighted code element
  const displayCode = getHighlightedCode();

  const handleCopy = () => {
    // Always copy the full code, not just the preview
    navigator.clipboard.writeText(fullCodeText.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Try to access ChatContext if available
  let fileHandler: ((filename: string, content: string, language: string) => void) | null = null;
  
  // Only activate the file handler if the code is long enough to warrant showing in canvas
  if (codeLines.length >= MIN_LINES_FOR_PREVIEW) {
    try {
      // Access global ChatContext if available
      const chatGlobal = (window as any).__CHAT_CONTEXT__;
      if (chatGlobal && typeof chatGlobal.handleFileSelect === 'function') {
        fileHandler = chatGlobal.handleFileSelect;
      }
    } catch (e) {
      // Silently ignore if context is not available
      console.debug('Chat context not available for file handling', e);
    }
  }

  // Handler for clicking on the file to open it in canvas
  const handleFileClick = () => {
    if (fileHandler) {
      // Pass the full code to the handler, not just the preview
      fileHandler(filename || 'code.txt', fullCodeText, language);
    }
  };
  
  // Handle action button click (open canvas on desktop)
  const handleActionButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileHandler) {
      // Pass the full code to the handler, not just the preview
      handleFileClick();
    }
  };

  // Check if we should show the action button - only if code is long enough
  const showActionButton = codeLines.length >= MIN_LINES_FOR_PREVIEW && 
    (fileHandler && filename) && 
    (!isMobile || isTablet) && 
    !isCanvasExpanded;

  return (
    <div 
      className={`relative group ${fontSizeClasses[preferences.fontSize || 'medium']}`}
      data-code-theme={['github', 'monokai'].includes(preferences.codeTheme || '') ? preferences.codeTheme : 'github'}
    >
      {/* Display filename if present with interactive elements when we have a file handler */}
      {filename && (
        <div 
          className={`flex items-center justify-between px-4 py-1 bg-muted/80 border-x border-t rounded-t-md text-xs font-mono ${fileHandler && !isCanvasExpanded ? 'cursor-pointer hover:bg-muted' : ''}`}
          onClick={(fileHandler && (!isMobile || isTablet) && !isCanvasExpanded) ? handleFileClick : undefined}
        >
          <div className="flex items-center gap-1">
            <Image 
              src={theme === "dark" ? "/images/logo-dark.svg" : "/images/logo.svg"} 
              alt="Logo" 
              width={14} 
              height={14} 
            />
            <span className="font-medium">{filename}</span>
            {!isCanvasExpanded && (
              <span className="text-xs text-muted-foreground ml-2">{language}</span>
            )}
          </div>
          {/* Only show expand/canvas options on tablet or desktop, not on mobile */}
          {fileHandler && !isCanvasExpanded && codeLines.length >= MIN_LINES_FOR_PREVIEW && !isMobile && (
            <div 
              className="flex items-center text-primary hover:text-primary/80"
              onClick={isMobile ? toggleExpanded : undefined}
            >
              <span className="text-xs mr-1">Open in canvas</span>
              <ArrowRight className="h-3 w-3" />
            </div>
          )}
        </div>
      )}
      
      {!filename && !isCanvasExpanded && (
        <div className="absolute top-0 right-0 p-2 text-xs text-muted-foreground font-mono">
          {language}
        </div>
      )}
      
      {/* Only show buttons when canvas is not expanded */}
      {!isCanvasExpanded && (
        <div className="absolute top-2 right-2 z-10 flex space-x-2">
          {/* Remove expand button for mobile devices */}
          
          {/* Expand canvas button - only if code is long enough and not on mobile */}
          {showActionButton && !isMobile && (
            <button 
              onClick={handleActionButtonClick}
              className="flex items-center p-2 bg-white/90 dark:bg-zinc-800/90 hover:bg-white dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md transition-all duration-200 hover:shadow-md hover:scale-105"
            >
              <Maximize2 className="h-4 w-4 text-zinc-500" />
              <span className="sr-only">View in canvas</span>
            </button>
          )}
          
          {/* Copy button */}
          <button 
            onClick={handleCopy}
            className="flex items-center p-2 bg-white/90 dark:bg-zinc-800/90 hover:bg-white dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md transition-all duration-200 hover:shadow-md hover:scale-105"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 text-zinc-500" />
            )}
            <span className="sr-only">Copy code</span>
          </button>
        </div>
      )}
      
      {/* Code display logic: 
         1. Always show the full code for short snippets (< 5 lines), regardless of canvas state
         2. For longer snippets, show the condensed "viewing in canvas" message when canvas is expanded
      */}
      {(!isCanvasExpanded || codeLines.length < MIN_LINES_FOR_PREVIEW) ? (
        <pre 
          className={`${className || ''} whitespace-pre-wrap ${filename ? 'mt-0 rounded-t-none' : 'mt-6'} rounded-md border ${fileHandler && filename && (!isMobile || isTablet) && codeLines.length >= MIN_LINES_FOR_PREVIEW ? 'cursor-pointer hover:border-primary/50' : ''}`} 
          {...props}
          onClick={(fileHandler && filename && (!isMobile || isTablet) && codeLines.length >= MIN_LINES_FOR_PREVIEW) ? handleFileClick : undefined}
        >
          {displayCode}
        </pre>
      ) : (
        <div className="border rounded-md py-2.5 px-4 bg-muted/20 text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image 
              src={theme === "dark" ? "/images/logo-dark.svg" : "/images/logo.svg"} 
              alt="Logo" 
              width={14} 
              height={14} 
            />
            <span className="font-medium">{filename}</span>
            <span className="text-xs text-muted-foreground">{language}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {codeLines.length} {codeLines.length === 1 ? 'line' : 'lines'} • Viewing in canvas
          </div>
        </div>
      )}
    </div>
  );
});

PreBlock.displayName = 'PreBlock';
